/**
 * Shared presentational bits for the community screens, composed from the
 * src/ui primitives + tokens only.
 *
 * NOTE ON ICONS: @expo/vector-icons is not installed in this workspace
 * (Expo SDK 57 no longer ships it with `expo`, and adding packages is out of
 * scope for this build). Icon moments therefore use neutral text glyphs in
 * the same spirit as src/ui (which uses ‹ and ›). Swap these for Feather
 * icons once the package is added.
 */
import { router } from 'expo-router';
import React from 'react';
import { Animated, Easing, Platform, Pressable, ScrollView, StyleSheet, View, type StyleProp, type ViewStyle, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { glow, radius, space } from '@bero/tokens';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Card, Pop, Skeleton } from '@/ui';

/** Back that survives cold web deep-links (no history → community tab). */
export function goBack() {
  if (router.canGoBack()) router.back();
  else router.replace('/(tabs)/community');
}

/* ---------------- sub tabs (underline) ---------------- */
export function SubTabs<T extends string>({ tabs, value, onChange }: {
  tabs: ReadonlyArray<readonly [T, string]>; value: T; onChange: (v: T) => void;
}) {
  const { colors } = useTheme();
  return (
    <View style={{ flexDirection: 'row', borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.borderSoft }}>
      {tabs.map(([key, label]) => {
        const active = value === key;
        return (
          <Pressable
            key={key}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            onPress={() => onChange(key)}
            style={{ flex: 1, paddingVertical: space.s3, alignItems: 'center' }}
          >
            <AppText variant="bodyMd" weight={active ? '600' : '500'} tone={active ? 'primary' : 'tertiary'}>
              {label}
            </AppText>
            {active ? (
              <View style={{ position: 'absolute', bottom: 0, width: '40%', height: 2, borderRadius: 2, backgroundColor: colors.primary500 }} />
            ) : null}
          </Pressable>
        );
      })}
    </View>
  );
}

/* ---------------- pill tabs (redesign v3: scrollable rounded chips) ----------------
 * Additive sibling of SubTabs (underline style) — used where the design shows
 * a horizontal row of filled/outline pill chips instead of an underline bar
 * (Community hub's Notices/Discussions/Polls/Events/Issues row). */
function PillTabButton({ label, active, onPress, colors }: {
  label: string; active: boolean; onPress: () => void; colors: ReturnType<typeof useTheme>['colors'];
}) {
  return (
    <Pressable
      accessibilityRole="tab"
      accessibilityState={{ selected: active }}
      onPress={onPress}
      style={{
        paddingHorizontal: 14, paddingVertical: 8, borderRadius: radius.full, borderWidth: 1,
        borderColor: active ? colors.primary500 : colors.border,
        backgroundColor: active ? colors.primary500 : colors.canvas0,
      }}
    >
      <AppText variant="bodySm" weight="600" style={{ color: active ? colors.canvas0 : colors.textSecondary }}>
        {label}
      </AppText>
    </Pressable>
  );
}

export function PillTabs<T extends string>({ tabs, value, onChange }: {
  tabs: ReadonlyArray<readonly [T, string]>; value: T; onChange: (v: T) => void;
}) {
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  // Desktop has no swipe affordance — wrap pills instead of clipping/scrolling.
  if (width >= 1024) {
    return (
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, paddingHorizontal: space.s4, paddingVertical: space.s3 }}>
        {tabs.map(([key, label]) => {
          const active = value === key;
          return (
            <PillTabButton key={key} label={label} active={active} onPress={() => onChange(key)} colors={colors} />
          );
        })}
      </View>
    );
  }
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={{ flexGrow: 0 }}
      contentContainerStyle={{ gap: 6, paddingHorizontal: space.s4, paddingVertical: space.s3, alignItems: 'center' }}
    >
      {tabs.map(([key, label]) => {
        const active = value === key;
        return (
          <Pressable
            key={key}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            onPress={() => onChange(key)}
            style={{
              paddingHorizontal: 14, paddingVertical: 8, borderRadius: radius.full, borderWidth: 1,
              borderColor: active ? colors.primary500 : colors.border,
              backgroundColor: active ? colors.primary500 : colors.canvas0,
            }}
          >
            <AppText variant="bodySm" weight="600" style={{ color: active ? colors.canvas0 : colors.textSecondary }}>
              {label}
            </AppText>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

/** Desktop width cap for screens that don't route their content through
 * `Screen`'s `padded` prop (which caps automatically at 960 on web ≥1024) —
 * e.g. a screen with its own custom outer container or a `scroll={false}`
 * shell. Wrap the screen's content in this to avoid a mobile-width column
 * pinned to the left edge of a wide viewport. No-op below 1024px. */
export function WebCap({ width = 960, children, style }: { width?: number; children: React.ReactNode; style?: StyleProp<ViewStyle> }) {
  const { width: winW } = useWindowDimensions();
  const wide = Platform.OS === 'web' && winW >= 1024;
  return <View style={[wide && { width: '100%', maxWidth: width, alignSelf: 'center' }, style]}>{children}</View>;
}

/* ---------------- arch avatar (redesign v3 brand motif) ----------------
 * Same initials-avatar contract as src/ui's Avatar, but arch-shaped
 * (large top radii, small bottom radii) per the brand rule: "avatars are
 * arch-shaped everywhere". Additive — src/ui's circular Avatar is untouched
 * and still used where a plain circle is wanted. */
export function ArchAvatar({ name, size = 40, tone = 'primary' }: {
  name: string; size?: number; tone?: 'primary' | 'accent' | 'filled' | 'neutral';
}) {
  const { colors } = useTheme();
  const initials = name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const map = {
    primary: { bg: colors.primary100, fg: colors.primary900 },
    accent: { bg: colors.accent100, fg: colors.accent700 },
    filled: { bg: colors.primary500, fg: colors.canvas0 },
    neutral: { bg: colors.canvas2, fg: colors.textSecondary },
  } as const;
  const { bg, fg } = map[tone];
  const h = Math.round(size * 1.14);
  const rTop = size / 2;
  const rBottom = Math.max(3, Math.round(size * 0.13));
  return (
    <View
      style={{
        width: size, height: h,
        borderTopLeftRadius: rTop, borderTopRightRadius: rTop,
        borderBottomLeftRadius: rBottom, borderBottomRightRadius: rBottom,
        backgroundColor: bg, alignItems: 'center', justifyContent: 'center',
      }}
    >
      <AppText weight="700" style={{ color: fg, fontSize: size * 0.34, lineHeight: size * 0.44 }}>
        {initials || '?'}
      </AppText>
    </View>
  );
}

/* ---------------- arch section label (redesign v3 brand motif) ----------------
 * SectionLabel (src/ui) + a small arch outline glyph beside it, per the brand
 * rule: "section-header glyph (small arch outline)". Additive — SectionLabel
 * itself is untouched. */
export function ArchSectionLabel({ children, style }: { children: React.ReactNode; style?: StyleProp<ViewStyle> }) {
  const { colors } = useTheme();
  return (
    <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 10 }, style]}>
      <View
        style={{
          width: 13, height: 8, borderTopLeftRadius: 7, borderTopRightRadius: 7,
          borderWidth: 1.5, borderBottomWidth: 0, borderColor: colors.primary500,
        }}
      />
      <AppText variant="label" tone="tertiary">{children}</AppText>
    </View>
  );
}

/* ---------------- urgent notice hero (redesign v3: the screen's one amber moment) ----------------
 * src/ui's NoticeCard tints "urgent" with danger (red) — correct for most
 * contexts, but the redesign explicitly gives the urgent-notice hero moment
 * amber (the brand's single sanctioned accent use per screen). This is a
 * distinct, additive component for that one hero spot (Home greeting band,
 * Community notices list) — NoticeCard is untouched and still used for every
 * other notice kind/card everywhere else. */
export function UrgentNoticeCard({ title, when, onPress }: { title: string; when?: string; onPress?: () => void }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => ({
        flexDirection: 'row', gap: space.s3, padding: space.s4, borderRadius: radius.lg,
        borderWidth: 1, borderColor: colors.accent500,
        ...glow(colors.accent500, 0.22, 18),
        backgroundColor: pressed ? colors.accent100 : colors.accent100,
        opacity: pressed ? 0.9 : 1,
      })}
    >
      <View style={{
        width: 34, height: 38, flexShrink: 0,
        borderTopLeftRadius: 17, borderTopRightRadius: 17, borderBottomLeftRadius: 4, borderBottomRightRadius: 4,
        backgroundColor: colors.accent500,
        alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name="megaphone" size={16} color={colors.canvas0} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <AppText variant="label" style={{ color: colors.accent700 }}>{t('urgent_notice')}</AppText>
        <AppText variant="bodyMd" weight="600" style={{ marginTop: 3 }}>{title}</AppText>
        <AppText variant="bodySm" weight="600" style={{ color: colors.accent700, marginTop: 5 }}>
          {t('acknowledge')} →{when ? `  ·  ${when}` : ''}
        </AppText>
      </View>
    </Pressable>
  );
}

/* ---------------- badges ---------------- */
export function ScopeBadge({ scope }: { scope: string }) {
  const { colors } = useTheme();
  return (
    <View style={{ backgroundColor: colors.infoTint, borderRadius: 10, paddingHorizontal: space.s2, paddingVertical: 2, alignSelf: 'flex-start' }}>
      <AppText variant="label" style={{ color: colors.info500, textTransform: 'none', letterSpacing: 0.2 }}>{scope}</AppText>
    </View>
  );
}

export function CatPill({ label }: { label: string }) {
  const { colors } = useTheme();
  return (
    <View style={{
      backgroundColor: colors.canvas1, borderWidth: StyleSheet.hairlineWidth, borderColor: colors.borderSoft,
      borderRadius: 10, paddingHorizontal: space.s2, paddingVertical: 2, alignSelf: 'flex-start',
    }}>
      <AppText variant="label" style={{ color: colors.textSecondary }}>{label}</AppText>
    </View>
  );
}

/* ---------------- FAB (56 circle, primary500, bottom-right 24) ---------------- */
export function Fab({ label, onPress }: { label: string; onPress: () => void }) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={({ pressed }) => ({
        position: 'absolute', right: 24, bottom: 24, width: 56, height: 56, borderRadius: radius.full,
        backgroundColor: pressed ? colors.primary600 : colors.primary500,
        alignItems: 'center', justifyContent: 'center', zIndex: 6,
        ...glow(colors.primary500, 0.4, 14),
      })}
    >
      <AppText style={{ color: colors.canvas0, fontSize: 26, lineHeight: 30 }} weight="500">+</AppText>
    </Pressable>
  );
}

/* ---------------- segmented control ---------------- */
export type SegOption = { key: string; label: string };

export function Segmented({ options, value, onChange, emphasis, grow }: {
  options: SegOption[]; value: string | null; onChange: (key: string) => void;
  /** active = primary500 fill (RSVP style) instead of canvas2 */
  emphasis?: boolean;
  /** stretch options to fill the row (no wrap) */
  grow?: boolean;
}) {
  const { colors } = useTheme();
  return (
    <View style={{ flexDirection: 'row', gap: 6, flexWrap: grow ? 'nowrap' : 'wrap' }}>
      {options.map((o) => {
        const active = value === o.key;
        return (
          <Pressable
            key={o.key}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            onPress={() => onChange(o.key)}
            style={[
              {
                minHeight: 44, paddingHorizontal: 14, paddingVertical: 9, borderRadius: radius.md,
                borderWidth: 1, alignItems: 'center', justifyContent: 'center',
                backgroundColor: active ? (emphasis ? colors.primary500 : colors.canvas2) : 'transparent',
                borderColor: active ? (emphasis ? colors.primary500 : colors.borderFirm) : colors.border,
              },
              grow ? { flex: 1, paddingHorizontal: space.s2 } : null,
            ]}
          >
            <AppText
              variant="bodySm"
              weight="500"
              style={{ color: active ? (emphasis ? colors.canvas0 : colors.textPrimary) : colors.textSecondary, textAlign: 'center' }}
            >
              {o.label}
            </AppText>
          </Pressable>
        );
      })}
    </View>
  );
}

/* ---------------- photo / cover placeholder ---------------- */
export function PhotoPlaceholder({ h = 120, label = 'photo', style }: {
  h?: number; label?: string; style?: StyleProp<ViewStyle>;
}) {
  const { colors } = useTheme();
  return (
    <View
      style={[
        {
          height: h, borderRadius: radius.lg, backgroundColor: colors.canvas1,
          borderWidth: StyleSheet.hairlineWidth, borderColor: colors.borderSoft,
          alignItems: 'center', justifyContent: 'center',
        },
        style,
      ]}
    >
      <AppText variant="bodySm" tone="tertiary" mono style={{ fontSize: 11 }}>{label}</AppText>
    </View>
  );
}

/** Dashed "add photo / cover" affordance for forms. */
export function AddPhotoBox({ label, h = 120 }: { label: string; h?: number }) {
  const { colors } = useTheme();
  return (
    <View
      style={{
        height: h, borderRadius: radius.lg, borderWidth: 1.5, borderStyle: 'dashed',
        borderColor: colors.borderFirm, backgroundColor: colors.canvas1,
        alignItems: 'center', justifyContent: 'center', gap: space.s1,
      }}
    >
      <AppText variant="headingMd" tone="tertiary">+</AppText>
      <AppText variant="bodySm" tone="tertiary">{label}</AppText>
    </View>
  );
}

/* ---------------- bottom action bar ---------------- */
export function BottomBar({ children }: { children: React.ReactNode }) {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  return (
    <View
      style={{
        borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.borderSoft,
        backgroundColor: colors.canvas0,
        paddingHorizontal: space.s4, paddingTop: space.s3,
        paddingBottom: Math.max(insets.bottom, space.s3) + space.s3,
      }}
    >
      {children}
    </View>
  );
}

/* ---------------- loading skeletons ---------------- */
export function CardSkeletons({ count = 3 }: { count?: number }) {
  return (
    <View style={{ gap: space.s3 }}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <Skeleton w="35%" h={12} />
          <Skeleton w="92%" h={16} style={{ marginTop: space.s3 }} />
          <Skeleton w="60%" h={12} style={{ marginTop: space.s2 }} />
        </Card>
      ))}
    </View>
  );
}

/* ---------------- horizontal filter chips row ---------------- */
export function ChipsRow({ children }: { children: React.ReactNode }) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={{ flexGrow: 0 }}
      contentContainerStyle={{ gap: space.s2, paddingHorizontal: space.s4, paddingVertical: space.s3, alignItems: 'center' }}
    >
      {children}
    </ScrollView>
  );
}

/* ---------------- poll option (radio) + result bar rows ---------------- */
export function PollOptionRow({ label, selected, onPress }: { label: string; selected: boolean; onPress: () => void }) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      onPress={onPress}
      style={{
        borderRadius: 10, borderWidth: 1.5,
        borderColor: selected ? colors.primary500 : colors.border,
        backgroundColor: selected ? colors.primary50 : colors.canvas0,
        paddingVertical: 14, paddingHorizontal: space.s4,
        flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 48,
      }}
    >
      <View
        style={{
          width: 20, height: 20, borderRadius: radius.full, borderWidth: 1.5,
          borderColor: selected ? colors.primary500 : colors.borderFirm,
          alignItems: 'center', justifyContent: 'center',
        }}
      >
        {selected ? <View style={{ width: 10, height: 10, borderRadius: radius.full, backgroundColor: colors.primary500 }} /> : null}
      </View>
      <AppText variant="bodyMd" style={{ flex: 1 }}>{label}</AppText>
    </Pressable>
  );
}

/**
 * Vote → results animation (redesign v3: "results bars animate .8s ease-out").
 * The bar fill grows from 0 to `pct` on mount/whenever `pct` changes, so the
 * same transition fires the moment a screen flips from the option list to
 * the results view after a vote (PollsTab / PollDetailScreen swap their JSX
 * on the same render the vote lands, which mounts this row fresh).
 */
export function PollResultRow({ label, pct, mine }: { label: string; pct: number; mine: boolean }) {
  const { colors } = useTheme();
  // Lazy state, not a ref: the Animated.Value is stable across renders and
  // reading it during render is legal (react-hooks/refs forbids ref reads).
  const [width] = React.useState(() => new Animated.Value(0));

  React.useEffect(() => {
    Animated.timing(width, {
      toValue: Math.max(0, Math.min(100, pct)),
      duration: 800,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [pct, width]);

  return (
    <View
      style={{
        position: 'relative', borderRadius: 10, borderWidth: StyleSheet.hairlineWidth,
        borderColor: colors.borderSoft, overflow: 'hidden', minHeight: 48, justifyContent: 'center',
      }}
    >
      <Animated.View
        style={{
          position: 'absolute', top: 0, bottom: 0, left: 0,
          width: width.interpolate({ inputRange: [0, 100], outputRange: ['0%', '100%'] }),
          backgroundColor: mine ? colors.primary100 : colors.canvas1,
        }}
      />
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 14, paddingHorizontal: space.s4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, flex: 1, minWidth: 0 }}>
          {mine ? <AppText variant="bodyMd" weight="600" tone="brand">✓</AppText> : null}
          <AppText variant="bodyMd" weight="500" numberOfLines={1} style={{ flexShrink: 1 }}>{label}</AppText>
        </View>
        <AppText variant="bodyMd" weight="600">{pct}%</AppText>
      </View>
    </View>
  );
}

/* ---------------- upvote pill ---------------- */
export function UpvotePill({ count, active, onPress }: { count: number; active: boolean; onPress: () => void }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <Pop trigger={active}>
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t('up_a11y')}
      accessibilityState={{ selected: active }}
      onPress={onPress}
      hitSlop={8}
      style={{
        flexDirection: 'row', alignItems: 'center', gap: 6,
        paddingHorizontal: space.s3, paddingVertical: 6, borderRadius: radius.full, borderWidth: 1,
        borderColor: active ? colors.primary500 : colors.border,
        backgroundColor: active ? colors.primary50 : 'transparent',
      }}
    >
      <AppText variant="bodySm" weight="500" style={{ color: active ? colors.primary500 : colors.textSecondary }}>
        ↑ {count}
      </AppText>
    </Pressable>
    </Pop>
  );
}

/* ---------------- date block (events list) ---------------- */
export function DateBlock({ day, month }: { day: string; month: string }) {
  const { colors } = useTheme();
  return (
    <View
      style={{
        width: 56, height: 56, borderRadius: radius.lg, backgroundColor: colors.canvas1,
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <AppText weight="700" style={{ fontSize: 22, lineHeight: 24 }}>{day || '·'}</AppText>
      <AppText variant="label" tone="tertiary" style={{ letterSpacing: 0 }}>{month}</AppText>
    </View>
  );
}

/* ---------------- opt-in toggle (birthdays) ---------------- */
export function TogglePill({ value, onChange, label }: { value: boolean; onChange: (v: boolean) => void; label: string }) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityLabel={label}
      accessibilityState={{ checked: value }}
      onPress={() => onChange(!value)}
      hitSlop={10}
      style={{ width: 46, height: 28, borderRadius: radius.full, backgroundColor: value ? colors.primary500 : colors.canvas2 }}
    >
      <View
        style={{
          position: 'absolute', top: 3, left: value ? 21 : 3, width: 22, height: 22,
          borderRadius: radius.full, backgroundColor: colors.textOnDark,
        }}
      />
    </Pressable>
  );
}
